const loadRoutes = () => {
  // Loaded on use, not at import. The route table imports the Usage page,
  // and this file is imported by Usage, so a top-level import would cycle.
  // eslint-disable-next-line global-require
  const loaded = require('config/routes');
  return loaded;
};

const routesFromConfig = () => {
  const loaded = loadRoutes();
  return loaded.default || loaded;
};

const sectionsFromConfig = () => loadRoutes().MENU_SECTIONS || [];

const searchParams = (search) => new URLSearchParams(String(search || '').replace(/^\?/, ''));

const normalisePath = (pathname) => String(pathname || '')
  .split('?')[0]
  .split('/')
  .map((segment) => (/^-?\d+$/.test(segment) ? ':id' : segment))
  .join('/');

/** Leaf route for a dashboard pathname. `:id` and numeric segments stay on the parent. */
const walk = (pathname, routes) => {
  const path = String(pathname || '').split('?')[0];
  if (!path.startsWith('/dashboard')) return null;
  const parts = path.substring('/dashboard/'.length).split('/');
  return parts.reduce((current, part) => {
    if (current === undefined) return undefined;
    if (!current) return routes.find(({ id }) => id === part);
    if (part === ':id' || /^-?\d+$/.test(part)) return current;
    if (current.subItems) return current.subItems.find(({ id }) => id === part);
    return current;
  }, null) || null;
};

const tabIdOf = (route, search) => {
  if (!route || !route.tabs) return null;
  const key = route.tabQueryLabel || 'tab';
  const value = searchParams(search).get(key);
  if (!value) return null;
  const matched = route.tabs.find(({ id, aliases }) => (
    id === value || (aliases || []).includes(value)
  ));
  return matched ? matched.id : value;
};

/**
 * Canonical usage path. Null when the route is unknown, or a tabbed route
 * has no tab param yet (the layout writes the default tab on the next pass).
 * Tab aliases collapse to the tab id, and every tab key is stored as `tab`.
 */
export const usagePath = (pathname, search = '', routes) => {
  const list = routes || routesFromConfig();
  const route = walk(pathname, list);
  if (!route) return null;
  const path = normalisePath(pathname);
  if (path === '/dashboard') return null;
  if (!route.tabs) return path;
  const tabId = tabIdOf(route, search);
  if (!tabId) return null;
  return `${path}?tab=${tabId}`;
};

const routeTitle = (route) => route.menuLabel || route.title || route.id || '';

const sectionName = (id, sections) => {
  if (!id) return null;
  const found = (sections || []).find((section) => section.id === id);
  return (found && found.label) || id;
};

/** Same shape DashboardLayout builds, with `:id` where usagePath stores a number. */
const catalogPath = (parentPath, route) => {
  let path = parentPath;
  if (route.pathParam) path += '/:id';
  if (route.id !== 'view') path += `/${route.id}`;
  return path;
};

const catalogEntry = (route, path, title, section, parentTitle) => ({
  path,
  title,
  section,
  hidden: Boolean(route.hidden || route.menuHidden),
  parentTitle: parentTitle || null,
  pathOverride: route.path || null,
});

/**
 * One row per page the route table can open.
 * Grouping parents (no component, no tabs) are skipped.
 * Tabbed routes emit `path?tab=<id>`, matching usagePath.
 * A custom `path` is flagged; the stored key stays the id path usage records.
 */
export const pageCatalog = (routes, sections) => {
  const list = routes || routesFromConfig();
  const sectionList = sections || sectionsFromConfig();
  const entries = [];

  const walkCatalog = (items, parentPath, section, parentTitle) => {
    (items || []).forEach((route) => {
      if (!route || !route.id || route.menuOnly) return;
      const nextSection = sectionName(route.section || section, sectionList);
      const path = catalogPath(parentPath, route);
      const title = String(routeTitle(route));
      const group = !route.Component && !route.tabs && route.subItems;
      if (!group) {
        if (route.tabs && route.tabs.length) {
          route.tabs.forEach((tab) => {
            const tabLabel = tab.label || tab.id;
            entries.push(catalogEntry(
              route,
              `${path}?tab=${tab.id}`,
              `${title} / ${tabLabel}`,
              nextSection,
              parentTitle,
            ));
          });
        } else {
          entries.push(catalogEntry(route, path, title, nextSection, parentTitle));
        }
      }
      if (route.subItems) walkCatalog(route.subItems, path, route.section || section, title);
    });
  };

  walkCatalog(list, '/dashboard', null, null);
  return entries;
};

/** Display name for a stored usage path. Unknown paths stay as stored. */
export const pageTitle = (storedPath, routes) => {
  const raw = String(storedPath || '');
  if (!raw) return raw;
  const [pathname, query = ''] = raw.split('?');
  const list = routes || routesFromConfig();
  const route = walk(pathname, list);
  if (!route) return raw;
  const title = route.menuLabel || route.title || route.id || raw;
  if (!route.tabs) return String(title);
  const tabValue = searchParams(query).get('tab');
  if (!tabValue) return String(title);
  const tab = route.tabs.find(({ id, aliases }) => (
    id === tabValue || (aliases || []).includes(tabValue)
  ));
  return `${title} / ${tab ? tab.label : tabValue}`;
};
